import { useEffect, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import { useNavigate } from "react-router-dom";
import Div100vh from "react-div-100vh";
import { useRecoilState } from "recoil";
import { userDataAtom } from "@/recoil/atom/userDataAtom";
import Header from "@/components/common/Header/header";
import { bankingingVals } from "./bankval";
import AAConfirmation from "./AAconfirmation";
import { trackConsent } from "@/lib/apis";
import { bankingStart, fetchData, getDataB2B } from "@/lib/helper";
import {
  fetchJourneyWithGates,
  evaluateGate,
  GATE_DECISION,
} from "@/b2b/lib/journeyGates";
import HeaderB2B from "@/b2b/components/Header";
import { ChevronDown, X } from "lucide-react";
import ProcessingScreen from "@/b2b/pages/processing";
import VerifyIncomeImage from "@/assets/images/why-verify-income.png";
import CustomDrawer from "@/components/common/drawer";
import GoBackButton from "@/components/common/buttons/GoBackButton.jsx";
import OyoIcon from "@/assets/icons/investors/oyo.png";
import RBI from "@/assets/icons/RBI.svg";
import BharatPeIcon from "@/assets/icons/investors/bharatpe.svg";
import RedLeaf from "@/assets/icons/investors/red-leaf.svg";
import PayuIcon from "@/assets/icons/investors/payu.png";
import UniIcon from "@/assets/icons/investors/uni-icon.png";
import GuardTick from "@/assets/icons/guard-tick.svg";
import { CONSENT_MESSAGES } from "@/lib/data.js";
import { useJourneyLender } from "@/lib/journeyLender";
import GromorConsentDialog from "@/b2b/components/GromorConsentDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const INVESTOR_ICONS = [RedLeaf, BharatPeIcon, OyoIcon, PayuIcon, UniIcon];

const ConsentDialog = ({ isOpen, onClose, onAllConsentsAgreed, theme, lender }) => {
  const [consents, setConsents] = useState({
    rentFinancing: true,
    householdIncome: true,
    bankingData: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const consentData = [
    {
      id: "rentFinancing",
      label: "Rent Financing Application",
      description: `I hereby apply for RENT FINANCING with ${lender.legalName} ("${lender.shortCode}") as per the details provided by me and I confirm to have read, understood and agreed to the`,
      links: [
        {
          text: "PRODUCT DETAILS",
          url: lender.productUrl,
        },
        {
          text: "APPLICATION TERMS",
          url: "/download/pdfs/APPLICATION_TERMS.pdf",
        },
      ],
      trackingKey: lender.legalName,
      consentMessage: lender.consents.apply,
    },
    {
      id: "householdIncome",
      label: "Household Income Confirmation",
      description: "I confirm that my household income is above ₹ 3,00,000/-",
      trackingKey: "Household Income",
      consentMessage: CONSENT_MESSAGES.FEXPRIME_INCOME,
    },
    {
      id: "bankingData",
      label: "Banking Data Access",
      description: "By continuing, I hereby agree to",
      links: [
        {
          text: "AA Terms of Service",
          url: lender.termsUrl,
        },
      ],
      additionalText: `and authorize ${lender.shortCode} to access my banking data via the RBI-licensed Account Aggregator network to assess credit eligibility`,
      trackingKey: "Banking",
      consentMessage: lender.consents.banking,
    },
  ];

  const handleConsentChange = (consentId, checked) => {
    setConsents((prev) => ({
      ...prev,
      [consentId]: checked,
    }));
  };

  const allConsentsChecked = Object.values(consents).every(
    (consent) => consent
  );

  const handleContinue = async () => {
    if (!allConsentsChecked) return;

    setIsSubmitting(true);
    try {
      const platform =
        navigator?.userAgentData?.platform ||
        navigator.platform ||
        localStorage.getItem("UTM_SOURCE") ||
        "web";

      const consentPromises = consentData.map((consent) =>
        trackConsent(
          "Onclick",
          consent.trackingKey,
          "",
          platform,
          consent.consentMessage
        ).catch((error) => {
          console.error(
            `Failed to track consent for ${consent.trackingKey}:`,
            error
          );
          return null;
        })
      );

      await Promise.allSettled(consentPromises);
      console.log("All consents tracked successfully from dialog");

      onAllConsentsAgreed();
      // Optional by design: the only call site (below) does not pass `onClose`, because the
      // dialog already closes through its `isOpen` prop once onAllConsentsAgreed flips
      // `consentsAgreed`. Calling it unconditionally threw "onClose is not a function" for
      // EVERY tenant reaching banking — caught by the catch below, so the flow survived, but
      // it logged a TypeError on every run and would have swallowed any real error raised
      // after this point. Optional-call keeps the existing behaviour and stays correct if a
      // future call site does pass one.
      onClose?.();
    } catch (error) {
      console.error("Error tracking consents:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent className="max-w-sm mx-auto bg-white border border-gray-200">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-base font-semibold text-gray-900">
            Terms & Consents
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {consentData.map((consent) => (
            <div key={consent.id} className="flex items-start gap-3">
              <input
                type="checkbox"
                id={consent.id}
                checked={consents[consent.id]}
                onChange={(e) =>
                  handleConsentChange(consent.id, e.target.checked)
                }
                className="accent-[#915DA5] w-4 h-4 border-gray-300 rounded"
              />
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={consent.id}
                  className="text-xs font-medium text-gray-900 cursor-pointer block mb-1"
                >
                  {consent.label}
                </label>
                <p className="text-xs text-gray-600 leading-relaxed">
                  <span>{consent.description}</span>
                  {consent.links &&
                    consent.links.map((link, linkIndex) => (
                      <span key={linkIndex}>
                        {linkIndex === 0 ? " " : " and "}
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 underline font-medium"
                        >
                          {link.text}
                        </a>
                      </span>
                    ))}
                  {consent.additionalText && (
                    <span> {consent.additionalText}</span>
                  )}
                  {consent.links && "."}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex pt-4 border-t border-gray-100 flex-col gap-2">
          <Button
            onClick={handleContinue}
            disabled={!allConsentsChecked || isSubmitting}
            className="w-full text-xs h-9 bg-[#915DA5] hover:bg-[#7c4791] text-white"
          >
            {isSubmitting ? "Processing..." : "Continue"}
          </Button>
          <div className="flex items-center justify-center">
            <span className="text-black text-2xs italic">powered by</span>
            <img
              src={lender.logoUrl}
              alt={lender.legalName}
              className="inline-block max-h-3 ml-1"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// const ConsentDrawer = ({ isOpen, onClose, onAllConsentsAgreed, theme }) => {
//   const [dynamicConsents, setDynamicConsents] = useState([]);

//   const fetchConsents = async () => {
//     try {
//       const consentsData = [
//         {
//           id: "rentFinancing",
//           description: 'I hereby apply for RENT FINANCING with Fexprime Finance Pvt. Ltd. ("FFPL") as per the details provided by me and I confirm to have read, understood and agreed to the',
//           links: [
//             { text: "PRODUCT DETAILS", url: "https://www.fexprime.com/Personal-LoanRent-Financing" },
//             { text: "APPLICATION TERMS", url: "/download/pdfs/APPLICATION_TERMS.pdf" }
//           ],
//           trackingKey: "Fexprime",
//           consentMessage: CONSENT_MESSAGES.FEXPRIME
//         },
//         {
//           id: "householdIncome",
//           description: "I confirm that my household income is above ₹ 3,00,000/-",
//           trackingKey: "Household Income",
//           consentMessage: CONSENT_MESSAGES.FEXPRIME_INCOME
//         },
//         {
//           id: "bankingData",
//           description: "By continuing, I hereby agree to",
//           links: [
//             { text: "AA Terms of Service", url: "https://fexprime.com/terms-service-aa" }
// ],
//           additionalText: "and authorize FFPL to access my banking data via the RBI-licensed Account Aggregator network to assess credit eligibility",
//           trackingKey: "Banking",
//           consentMessage: CONSENT_MESSAGES.BANKING
//         }
//       ];

//       setDynamicConsents(consentsData);

//       if (onAllConsentsAgreed) {
//         onAllConsentsAgreed();
//       }
//     } catch (error) {
//       console.error("Failed to load consents:", error)
//     }
//   }

//   useEffect(() => {
//     if (isOpen && dynamicConsents.length === 0) {
//       fetchConsents()
//     }
//   }, [isOpen])

//   if (!isOpen) return null

//   return (
//     <>
//       <div className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300" onClick={onClose} />

//       <div className="fixed inset-x-4 bottom-4 z-50 bg-white rounded-xl shadow-2xl transform transition-transform duration-300 ease-out max-w-md mx-auto">
//         <div className="max-h-[70vh] flex flex-col">
//           <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
//             <h3 className="text-lg font-medium text-gray-900">Terms & Consents</h3>
//             <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
//               <X className="w-4 h-4 text-gray-400" />
//             </button>
//           </div>

//           <div className="flex-1 overflow-y-auto px-4 py-3">
//             <div className="space-y-3">
//               {dynamicConsents.map((consent, index) => (
//                 <div key={consent.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50/30">
//                   <div className="text-xs text-gray-700 leading-relaxed">
//                     <span>{consent.description}</span>
//                     {consent.links &&
//                       consent.links.map((link, linkIndex) => (
//                         <span key={linkIndex}>
//                           {linkIndex === 0 ? " " : " and other relevant "}
//                           <a
//                             href={link.url}
//                             target="_blank"
//                             rel="noopener noreferrer"
//                             className="text-blue-600 hover:text-blue-700 underline font-medium"
//                           >
//                             {link.text}
//                           </a>
//                           {linkIndex === consent.links.length - 1 ? "" : ""}
//                         </span>
//                       ))}
//                     {consent.additionalText && <span> {consent.additionalText}</span>}
//                     {consent.links && "."}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   )
// }

export default function BankDetails({ theme }) {
  const navigate = useNavigate();
  const [selectedBank, setSelectedBank] = useState("");
  const [availableMethods, setAvailableMethods] = useState({});
  const [selectedMethod, setSelectedMethod] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [phone, setPhone] = useState("");
  const [linkDrawer, setLinkDrawer] = useState(false);
  // const [showConsentDrawer, setShowConsentDrawer] = useState(false)
  const [consentsAgreed, setConsentsAgreed] = useState(false);
  const [showInitialConsentDialog, setShowInitialConsentDialog] =
    useState(false);
  const [userData, setUserData] = useRecoilState(userDataAtom);
  const [journeyData, setJourneyData] = useState(null);
  // Null until the journey has said which lender it is; the consent dialogs name the lender.
  const lender = useJourneyLender(journeyData?.lender);
  const token = localStorage.getItem("user_identifier");
  const partner = localStorage.getItem("partnerIdentifier");

  const startOnboardingJourney = async () => {
    if (!token) return;

    try {
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const result = await getDataB2B("b2b/onboard/start", token);
      console.log("Onboarding Start Result:", result);
      setJourneyToken(result?.token);

      // `bank_eligible` is a CACHED MIRROR of the banking step on the Lead, written when
      // banking succeeds and never reset. 216 production leads carry it as `true` while their
      // BANKING step is not SUCCESS, so forwarding on it alone waves a tenant straight past
      // the very step they are standing on — including one who was just correctly refused
      // Congratulations, which simply relocates the leak instead of closing it.
      //
      // So the flag still decides WHERE to send them (its routing intent is unchanged), but the
      // server decides WHETHER they may be sent at all. The token is set above because
      // /journey/full needs it.
      if (result?.user?.bank_eligible === true) {
        let mayProceed = true;
        try {
          const { gates } = await fetchJourneyWithGates();
          mayProceed =
            evaluateGate(gates, "postBanking").decision !== GATE_DECISION.REDIRECT;
        } catch (error) {
          // Fail open, matching every other gate call site: the server withholds by ANSWERING,
          // so no answer at all is an outage, and blocking a tenant here would strand them on
          // a screen they have already completed.
          console.error("Could not verify banking before forwarding", error);
        }

        if (mayProceed) {
          if (result?.user?.rentSummaryConsent) {
            navigate(
              `/b/${token}?partner=${result?.user?.source}&step=onboarding`
            );
          } else {
            navigate(`/b/${token}?partner=${result?.user?.source}&step=congrats`);
          }
        } else {
          console.warn(
            "Skipped the post-banking forward: the server reports banking is not complete",
          );
        }
      }
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const canUserDoBanking = async () => {
    try {
      const response = await fetchData("banking/banking-check-status");

      return response?.bankingStatus;
    } catch (error) {
      console.error("Error fetching data", error);
      return false;
    }
  };

  const getUserData = async () => {
    try {
      const data = await fetchData("user/me");
      setUserData(data);

      const { journey } = await fetchData("journey/full");

      console.log("Full Journey Data:", journey);
      setJourneyData(journey);
      if (
        !partner &&
        !journey?.employment?.workEmail &&
        journey?.employment?.employmentType !== "SELF_EMPLOYED"
      ) {
        navigate("/work-details");
        return;
      }

      if (journey?.lender === "FEXPRIME" || journey?.lender === "GROMOR") {
        setShowInitialConsentDialog(true);
        if (journey?.banking?.length > 0) {
          handleAllConsentsAgreed();
        }
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  useEffect(() => {
    const initiateProcess = async () => {
      try {
        setLoading(true);
        if (token) {
          await startOnboardingJourney();
        }
        const canDoBanking = await canUserDoBanking();
        if (!canDoBanking) {
          if (token)
            navigate(`/b/${token}?partner=${partner}&step=banking-limit`);
          else navigate("/banking-limit");
          return;
        }
        await getUserData();
      } catch (error) {
        console.error("Error during operation:", error);
      } finally {
        setLoading(false);
      }
    };

    initiateProcess();
  }, []);

  const handleBankSelection = (event) => {
    const bankKey = event.target.value;
    console.log("Selected bank:", bankKey);
    if (selectedBank === bankKey) return;
    if (selectedBank !== bankKey) setSelectedMethod("");
    setSelectedBank(bankKey);

    const bankDetails = bankingingVals.find(
      (bank) => bank.institution_id.toString() === bankKey
    );
    console.log("Bank details:", bankDetails);
    if (bankDetails) {
      setAvailableMethods(bankDetails);
    } else {
      setAvailableMethods([]);
    }
  };

  const handleNext = async () => {
    try {
      setLoading(true);

      if (selectedMethod === "AA") {
        setShowDialog(true);
      } else {
        const response = await bankingStart("banking/start", {
          institution_id: selectedBank,
          method: selectedMethod,
        });
        window.location.href = response.sessionUrl;
      }
    } catch (error) {
      console.log("Error during operation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAAConfirm = async (altPhone) => {
    try {
      setLoading(true);
      setShowDialog(false);
      const body = {
        institution_id: availableMethods["institution_id"],
        method: selectedMethod,
        phone: altPhone || phone,
        fip: availableMethods["fip-id"],
        selectedBank: availableMethods["name"],
      };
      const response = await bankingStart("banking/start", body);
      window.location.href = response.sessionUrl;
    } catch (error) {
      console.log("Error during operation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAllConsentsAgreed = () => {
    setConsentsAgreed(true);
    console.log("All consents have been tracked and agreed to!");
  };

  // 🔴 Do NOT add `recoverable` here. This covers `POST banking/start` while the tenant is being
  // handed off to the vendor. A reload can open a SECOND banking session, and every session spends
  // one of the tenant's few allowed attempts — the precise harm the attempt-limit work exists to
  // prevent. A tenant stuck here should be moved by ops, not by a button that quietly costs them a
  // try. (The read-only sibling `banking/home.jsx` does opt in; the difference is the POST.)
  if (loading) return <ProcessingScreen theme={theme} />;

  if (!userData) {
    return null;
  }

  if (partner || userData.employment) {
    return (
      <>
        {lender &&
          (journeyData?.lender === "GROMOR" ? (
            <GromorConsentDialog
              isOpen={showInitialConsentDialog && !consentsAgreed}
              onAllConsentsAgreed={handleAllConsentsAgreed}
              lender={lender}
            />
          ) : (
            <ConsentDialog
              isOpen={showInitialConsentDialog && !consentsAgreed}
              onAllConsentsAgreed={handleAllConsentsAgreed}
              theme={theme}
              lender={lender}
            />
          ))}

        <Div100vh
          className={`flex flex-col justify-start space-y-8 p-10 ${
            theme?.backgroundColor && theme.backgroundColor
          }`}
        >
          {lender && showInitialConsentDialog && !consentsAgreed && (
            <div className="fixed inset-0 bg-black/50 z-40" />
          )}

          <div className="flex flex-col justify-between h-full">
            <div className="flex flex-col justify-between">
              {theme?.textColor ? (
                <HeaderB2B
                  heading="Quick Income Check"
                  subheading={partner === "kots" ? "Just one step left, link your bank account to continue." : "You're one step away from enabling zero-deposit move-in at your property."}
                />
              ) : (
                <Header
                  heading="Quick Income Check"
                  subheading="We do require your 6 months banking statement to analyze salary and income."
                />
              )}
              {/* 
              {consentsAgreed && (
                <> */}

              <div className="flex flex-col gap-2">
                {token && (
                  <button onClick={() => setLinkDrawer(true)}>
                    <img src={VerifyIncomeImage || "/placeholder.svg"} alt="" />
                  </button>
                )}
                <label
                  className={`subheading block mb-1 ${
                    theme?.textColor
                      ? `${theme?.textColor}`
                      : "text-primary-200"
                  }`}
                >
                  Select your salary bank account
                </label>

                <div className="relative w-full">
                  <select
                    name="Select your salary bank"
                    onChange={handleBankSelection}
                    value={selectedBank}
                    required
                    className={`w-full appearance-none border border-primary-500 h-14 shadow-sm 
                    focus:ring-primary-500 focus:border-primary-500 px-4 py-2.5 pr-10 pl-12
                    transition-all duration-200 ease-in-out ${
                      theme?.backgroundColor
                        ? `${theme?.backgroundColor} ${theme?.textColor}`
                        : "bg-input-bg"
                    }`}
                  >
                    <option value="" disabled>
                      Select bank
                    </option>
                    {bankingingVals.map((option, index) => (
                      <option key={index} value={option.institution_id}>
                        {option.name}
                      </option>
                    ))}
                  </select>

                  <ChevronDown
                    className="absolute top-1/2 right-4 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                    size={20}
                  />
                </div>
              </div>

              {selectedBank && (
                <div className="mt-6 flex flex-col gap-4 justify-between h-full">
                  <div className="flex flex-col gap-2">
                    <span
                      className={`${
                        theme?.textColor ? `${theme?.textColor}` : ""
                      }`}
                    >
                      Select a method
                    </span>
                    {availableMethods.methods?.map((method, index) =>
                      method === "AA" && journeyData?.lender !== "FEXPRIME" ? (
                        <></>
                      ) : (
                        <div
                          className="relative flex flex-col gap-1"
                          key={index}
                        >
                          <button
                            className={`px-4 py-3 rounded border transition-all duration-200 ease-in-out relative ${
                              theme?.primaryButtonBgColor
                                ? selectedMethod === method
                                  ? `${theme?.primaryButtonBgColor}`
                                  : `${theme?.backgroundColor} ${theme?.headingColor} ${theme?.borderColor}`
                                : selectedMethod === method
                                ? "bg-white text-black border-white"
                                : "bg-black text-white border-gray-500"
                            }`}
                            onClick={() => setSelectedMethod(method)}
                          >
                            {method === "AA"
                              ? "Verify via OTP"
                              : method === "PDF"
                              ? "Statement PDF Upload"
                              : "Net Banking"}
                          </button>

                          {method === "AA" && (
                            <span className="absolute top-1 -left-1 bg-green-600 font-semibold tracking-wider text-white text-[9px] px-2 py-1 rounded-r-full shadow-md">
                              Recommended
                            </span>
                          )}
                        </div>
                      )
                    )}
                  </div>

                  {selectedMethod && (
                    <>
                      <button
                        onClick={handleNext}
                        className={`py-4 px-4 font-medium rounded-full transition-all duration-200 my-3 ${
                          theme?.primaryButtonBgColor
                            ? `${theme?.primaryButtonBgColor}`
                            : "text-black bg-white"
                        } border border-white ${
                          loading
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:opacity-90"
                        }`}
                        disabled={loading}
                      >
                        {loading ? "Loading..." : "Continue"}
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* </>
              )} */}

              <AAConfirmation
                open={showDialog}
                setOpen={setShowDialog}
                phone={userData?.phone}
                onConfirm={handleAAConfirm}
                theme={theme}
              />

              {/* <ConsentDrawer
                isOpen={showConsentDrawer}
                onClose={() => setShowConsentDrawer(false)}
                onAllConsentsAgreed={handleAllConsentsAgreed}
                theme={theme}
              /> */}

              <CustomDrawer isOpen={linkDrawer} size="85%" roundedTop>
                <div className="flex flex-col bg-white h-full py-6 px-10 justify-between">
                  <div>
                    <GoBackButton
                      customBackSubmit={() => setLinkDrawer(false)}
                    />
                    <div className="flex flex-col gap-4 mt-6">
                      <span className="font-semibold text-zinc-500 text-lg font-normal uppercase">
                        HEY {userData?.firstName}
                      </span>
                      <span className="text-neutral-700 text-lg font-normal">
                        We just need to verify if you can comfortably afford the
                        rent.
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-8">
                    <div className="flex flex-col gap-12">
                      <div className="gap-1 text-zinc-400 px-8 text-center">
                        <span>CirclePe has already helped </span>{" "}
                        <span className="font-semibold text-black">
                          20,000+
                        </span>{" "}
                        <span>tenants across India</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-zinc-400 px-4 text-center">
                          Backed by India's best Investors
                        </span>
                        <div className="flex justify-evenly items-center space-x-4 mt-2">
                          {INVESTOR_ICONS.map((icon, index) => (
                            <img
                              className={`${
                                [OyoIcon].includes(icon) ? "max-h-6" : "max-h-8"
                              }`}
                              src={icon || "/placeholder.svg"}
                              key={index}
                              alt="investor"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-2 items-center px-4">
                      <div className="inline-flex items-center gap-2 text-xs">
                        <img
                          src={GuardTick || "/placeholder.svg"}
                          className="max-h-6 inline-block"
                          alt=""
                        />
                        <span className="text-neutral-700 items-center">
                          Your data is{" "}
                          <span className="text-[#2CA471]">
                            100% safe
                            <br />
                          </span>{" "}
                          We are regulated by{" "}
                          <img
                            src={RBI || "/placeholder.svg"}
                            className="max-h-6 inline-block mx-1"
                            alt=""
                          />
                          <span className="font-semibold text-neutral-700">
                            RBI
                          </span>
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setLinkDrawer(false)}
                      className={`py-3 px-4 font-medium bg-[#2CA471] border border-white rounded-full`}
                    >
                      Okay Let's proceed
                    </button>
                  </div>
                </div>
              </CustomDrawer>
            </div>
            {lender && (
              <div
                className={`w-full text-center text-[0.678rem] ${
                  theme?.text || "text-[#aeaeae]"
                }`}
              >
                Powered by{" "}
                <a
                  target="_blank"
                  href={lender.homeUrl}
                  className="hover:underline hover:text-primary inline-flex items-center"
                  rel="noreferrer"
                >
                  <img
                    src={lender.logoUrl}
                    alt={lender.legalName}
                    className="inline-block max-h-4 ml-1"
                  />
                </a>
              </div>
            )}
          </div>
        </Div100vh>
      </>
    );
  }
}
